import axios from "axios";
import { CalendarIcon } from "lucide-react";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FaBuilding,
  FaUser,
  FaBriefcase,
  FaMoneyBillWave,
  FaFileSignature,
  FaUpload,
  FaXmark,
  FaClipboardCheck,
} from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../payslip/payslippicker.css";

const steps = [
  {
    id: 1,
    title: "Company\nInformation",
    mobileTitle: "Company\nInformation",
    icon: FaBuilding,
    fields: [
      "companyName",
      "companyEmail",
      "companyPhone",
      "companyWebsite",
      "companyAddress",
      "companyLogo",
    ],
  },
  {
    id: 2,
    title: "Employee\nInformation",
    mobileTitle: "Employee\nInformation",
    icon: FaUser,
    fields: [
      "employeeName",
      "employeeId",
      "employeePhone",
      "employeeEmail",
      "residentialAddress",
      "currentDesignation",
      "currentDepartment",
      "workLocation",
      "reportingManager",
    ],
  },
  {
    id: 3,
    title: "Probation &\nConfirmation Details",
    mobileTitle: "Probation &\nConfirmation Details",
    icon: FaClipboardCheck,
    fields: [
      "joiningDate",
      "originalProbationPeriod",
      "probationCompletionDate",
      "confirmationEffectiveDate",
      "confirmationType",
      "employmentStatusAfterConfirmation",
      "confirmedDesignation",
      "probationExtensionDetails",
    ],
  },
  {
    id: 4,
    title: "Salary &\nEmployment Terms",
    mobileTitle: "Salary &\nEmployment Terms",
    icon: FaMoneyBillWave,
    fields: [
      "revisedAnnualCTC",
      "salaryRevisionEffectiveDate",
      "additionalBenefits",
      "noticePeriodAfterConfirmation",
      "workingDays",
      "workingHours",
      "leaveEntitlement",
      "existingTermsContinue",
      "additionalEmploymentConditions",
    ],
  },
  {
    id: 5,
    title: "Performance\nDetails",
    mobileTitle: "Performance\nDetails",
    icon: FaBriefcase,
    fields: [
      "performanceRating",
      "appreciationStatement",
      "keyStrengths",
      "futureExpectations",
    ],
  },
  {
    id: 6,
    title: "Letter\nInformation",
    mobileTitle: "Letter\nInformation",
    icon: FaFileSignature,
    fields: [
      "confirmationLetterDate",
      "referenceNumber",
      "signatoryName",
      "signatoryDesignation",
      "acknowledgementRequired",
    ],
  },
];

const defaultValues = {
  // Company
  companyName: "",
  companyEmail: "",
  companyPhone: "",
  companyWebsite: "",
  companyAddress: "",
  companyLogo: null,

  // Employee
  employeeName: "",
  employeeId: "",
  employeePhone: "",
  employeeEmail: "",
  residentialAddress: "",
  currentDesignation: "",
  currentDepartment: "",
  workLocation: "",
  reportingManager: "",

  // Probation & Confirmation
  joiningDate: "",
  originalProbationPeriod: "",
  probationCompletionDate: "",
  confirmationEffectiveDate: "",
  confirmationType: "",
  employmentStatusAfterConfirmation: "",
  confirmedDesignation: "",
  probationExtensionDetails: "",

  // Salary & Employment Terms
  revisedAnnualCTC: "",
  salaryRevisionEffectiveDate: "",
  additionalBenefits: "",
  noticePeriodAfterConfirmation: "",
  workingDays: "",
  workingHours: "",
  leaveEntitlement: "",
  existingTermsContinue: "Yes",
  additionalEmploymentConditions: "",

  // Performance
  performanceRating: "",
  appreciationStatement: "",
  keyStrengths: "",
  futureExpectations: "",

  // Letter
  confirmationLetterDate: "",
  referenceNumber: "",
  signatoryName: "",
  signatoryDesignation: "",
  acknowledgementRequired: "Yes",
};

const inputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#10324F] outline-none transition placeholder:text-slate-400 focus:border-0 focus:ring-0";

const labelClass = "mb-1.5 block text-sm font-medium text-[#10324F]";

const FormField = ({ label, required, error, children }) => {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      {children}

      {error && (
        <p className="mt-1 text-[10px] text-red-500">{error.message}</p>
      )}
    </div>
  );
};

const parseDateValue = (value) => {
  if (!value) return null;

  const [year, month, day] = String(value).split("-").map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const formatDateValue = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const DateField = ({ name, label, required, control, errors }) => (
  <FormField label={label} required={required} error={errors[name]}>
    <Controller
      name={name}
      control={control}
      rules={
        required
          ? {
              required: `${label} is required`,
            }
          : {}
      }
      render={({ field }) => (
        <DatePicker
          selected={parseDateValue(field.value)}
          onChange={(date) => {
            field.onChange(formatDateValue(date));
          }}
          onBlur={field.onBlur}
          dateFormat="yyyy-MM-dd"
          showPopperArrow={false}
          isClearable
          placeholderText="Select date"
          showIcon
          toggleCalendarOnIconClick
          icon={<CalendarIcon className="h-4 w-4" />}
          className={`${inputClass} h-10`}
          wrapperClassName="w-full"
          popperClassName="payslip-datepicker-popper react-popper-offset"
          calendarClassName="payslip-datepicker"
          popperPlacement="top-start"
          portalId="datepicker-portal"
        />
      )}
    />
  </FormField>
);

const BASE_URL = import.meta.env.VITE_BASE_URL;

const ConfirmationLetterGeneratorForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [logoPreview, setLogoPreview] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  const values = watch();

  const currentStep = steps[activeStep];

  const handleNext = async () => {
    const valid = await trigger(currentStep.fields);

    // Company logo is manually handled
    if (activeStep === 0 && !logoPreview) {
      setError("companyLogo", {
        type: "required",
        message: "Company logo is required",
      });

      return;
    }

    if (!valid) return;

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
    clearErrors("companyLogo");
  };

  const onSubmit = async (data) => {
    try {
      if (isGenerating) return;

      setIsGenerating(true);

      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "companyLogo") {
          if (value instanceof File) {
            formData.append("companyLogo", value);
          }
        } else {
          formData.append(key, value ?? "");
        }
      });

      const response = await axios.post(
        `${BASE_URL}/generate-confirmationletter`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      const contentType = response.headers["content-type"];

      if (!contentType?.includes("application/pdf")) {
        const text = await response.data.text();

        console.error("Backend returned:", text);

        throw new Error("Backend did not return a Confirmation Letter PDF");
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `Confirmation-Letter-${data.employeeName || "Employee"}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.open(url, "_blank");

      // reset(defaultValues);

      setLogoPreview("");

      setActiveStep(0);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error("Confirmation letter generation failed:", error);

      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();

        try {
          const errorData = JSON.parse(text);

          alert(errorData?.message || "Failed to generate Confirmation Letter");
        } catch {
          alert("Failed to generate Confirmation Letter");
        }
      } else {
        alert(error?.message || "Failed to generate Confirmation Letter");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const clearLogo = () => {
    setLogoPreview("");
    setValue("companyLogo", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <section>
      <form className="w-full">
        <div className="grid grid-cols-1 gap-2 xl:grid-cols-[65%_34%]">
          {/* ================= FORM ================= */}
          <div className="min-w-0 rounded-xl border border-white  shadow-sm backdrop-blur-2xl p-4">
            {/* STEP NAVIGATION */}
            <div className="scrollbar-hide flex w-full gap-2 overflow-x-auto pb-2 sm:overflow-visible">
              {steps.map((step, index) => {
                const active = activeStep === index;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={async () => {
                      if (index <= activeStep) {
                        setActiveStep(index);
                        return;
                      }

                      const valid = await trigger(steps[activeStep].fields);

                      if (valid) {
                        setActiveStep(index);
                      }
                    }}
                    className={`flex min-w-26.25 shrink-0 flex-1 items-center justify-center rounded-md px-2 py-2.5 text-center transition                       
          sm:min-w-0 
          ${
            active
              ? " bg-[#10324F] text-white shadow-sm"
              : "border border-white  text-[#10324F] bg-linear-to-b from-white/60 to-[#D1DFEA]/30  shadow-sm hover:bg-white/70"
          }`}
                  >
                    <span className="whitespace-pre-line text-xs leading-4 sm:text-sm">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* CURRENT STEP */}
            <div className="mt-5">
              {activeStep === 0 && (
                <CompanyInformation
                  register={register}
                  errors={errors}
                  handleLogoChange={handleLogoChange}
                  logoPreview={logoPreview}
                  clearLogo={clearLogo}
                />
              )}

              {activeStep === 1 && (
                <EmployeeInformation register={register} errors={errors} />
              )}

              {activeStep === 2 && (
                <ProbationConfirmationDetails
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 3 && (
                <SalaryEmploymentTerms
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 4 && (
                <PerformanceDetails register={register} errors={errors} />
              )}

              {activeStep === 5 && (
                <LetterInformation
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}
            </div>

            {/* NAVIGATION BUTTONS */}
            <div className="mt-7 flex items-center justify-center gap-3">
              {activeStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="min-w-30 rounded-md border border-[#163957] px-6 py-2.5 text-sm font-medium text-[#163957] transition hover:bg-[#163957] hover:text-white"
                >
                  Previous
                </button>
              )}

              {activeStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="min-w-30 rounded-md bg-[#163957] px-8 py-2.5 text-sm font-medium text-white transition hover:bg-[#10324F]"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={handleSubmit(onSubmit)}
                  className={`min-w-45 rounded-md px-8 py-2.5 text-sm font-medium text-white transition ${
                    isGenerating
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-[#10324F] hover:bg-[#10324F]"
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </span>
                  ) : (
                    "Generate Confirmation Letter"
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ================= PREVIEW ================= */}
          <ConfirmationLetterPreview
            values={values}
            logoPreview={logoPreview}
          />
        </div>
      </form>
    </section>
  );
};

export default ConfirmationLetterGeneratorForm;

const CompanyInformation = ({
  register,
  errors,
  handleLogoChange,
  logoPreview,
  clearLogo,
}) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Company Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Company Name" required error={errors.companyName}>
          <input
            {...register("companyName", {
              required: "Company name is required",
              minLength: {
                value: 2,
                message: "Enter a valid company name",
              },
            })}
            className={inputClass}
            placeholder="Enter Company name"
          />
        </FormField>

        <FormField label="Company Email" required error={errors.companyEmail}>
          <input
            type="email"
            {...register("companyEmail", {
              required: "Company email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Email"
          />
        </FormField>

        <FormField label="Phone Number" required error={errors.companyPhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("companyPhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Website" error={errors.companyWebsite}>
          <input
            {...register("companyWebsite", {
              required: "Company website is required",
              pattern: {
                value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
                message: "Enter valid website",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Website"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Company Address"
            required
            error={errors.companyAddress}
          >
            <input
              {...register("companyAddress", {
                required: "Company address is required",
              })}
              className={inputClass}
              placeholder="Enter Company Address"
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField label="Company Logo" required error={errors.companyLogo}>
            {logoPreview ? (
              <div className="w-60">
                <div className="relative flex h-16 w-full items-center justify-center rounded-md border border-slate-200 bg-white">
                  <img
                    src={logoPreview}
                    alt="Company logo"
                    className="h-14 max-w-55 object-contain"
                  />

                  <button
                    type="button"
                    onClick={clearLogo}
                    className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600"
                    aria-label="Remove logo"
                  >
                    <FaXmark className="text-xs" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex h-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white transition hover:bg-slate-50">
                <div className="text-center text-[10px] text-[#10324F]">
                  <FaUpload className="mx-auto mb-1 text-[#10324F]/30" />
                  Upload Logo
                </div>

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </label>
            )}
          </FormField>
        </div>
      </div>
    </div>
  );
};

const EmployeeInformation = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Employee Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Employee Full Name"
          required
          error={errors.employeeName}
        >
          <input
            {...register("employeeName", {
              required: "Employee name is required",
            })}
            className={inputClass}
            placeholder="Enter employee name"
          />
        </FormField>

        <FormField label="Employee ID" required error={errors.employeeId}>
          <input
            {...register("employeeId", {
              required: "Employee ID is required",
            })}
            className={inputClass}
            placeholder="Enter employee ID"
          />
        </FormField>

        <FormField label="Phone Number" error={errors.employeePhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("employeePhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter phone number"
          />
        </FormField>

        <FormField label="Email Address" error={errors.employeeEmail}>
          <input
            type="email"
            {...register("employeeEmail", {
              required: "Employee email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter email address"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Residential Address"
            required
            error={errors.residentialAddress}
          >
            <input
              {...register("residentialAddress", {
                required: "Residential address is required",
              })}
              className={inputClass}
              placeholder="Enter address"
            />
          </FormField>
        </div>

        <FormField
          label="Current Designation"
          required
          error={errors.currentDesignation}
        >
          <input
            {...register("currentDesignation", {
              required: "Current designation is required",
            })}
            className={inputClass}
            placeholder="Enter current designation"
          />
        </FormField>

        <FormField label="Current Department" error={errors.currentDepartment}>
          <input
            {...register("currentDepartment")}
            className={inputClass}
            placeholder="Enter department"
          />
        </FormField>

        <FormField label="Work Location" error={errors.workLocation}>
          <input
            {...register("workLocation")}
            className={inputClass}
            placeholder="Enter work location"
          />
        </FormField>

        <FormField
          label="Current Reporting Manager"
          error={errors.reportingManager}
        >
          <input
            {...register("reportingManager")}
            className={inputClass}
            placeholder="Enter reporting manager"
          />
        </FormField>
      </div>
    </div>
  );
};

const ProbationConfirmationDetails = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Probation and Confirmation Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          name="joiningDate"
          label="Date of joining"
          required
          control={control}
          errors={errors}
        />

        <FormField
          label="Original probation period"
          required
          error={errors.originalProbationPeriod}
        >
          <select
            {...register("originalProbationPeriod", {
              required: "Probation period is required",
            })}
            className={inputClass}
          >
            <option value="">Select probation period</option>
            <option value="1 Month">1 Month</option>
            <option value="2 Months">2 Months</option>
            <option value="3 Months">3 Months</option>
            <option value="6 Months">6 Months</option>
            <option value="9 Months">9 Months</option>
            <option value="12 Months">12 Months</option>
          </select>
        </FormField>

        <DateField
          name="probationCompletionDate"
          label="Probation completion date"
          required
          control={control}
          errors={errors}
        />

        <DateField
          name="confirmationEffectiveDate"
          label="Confirmation effective date"
          required
          control={control}
          errors={errors}
        />

        <FormField
          label="Confirmation type"
          required
          error={errors.confirmationType}
        >
          <select
            {...register("confirmationType", {
              required: "Confirmation type is required",
            })}
            className={inputClass}
          >
            <option value="">Select type</option>
            <option value="Confirmation on Completion of Probation">
              Confirmation on Completion of Probation
            </option>
            <option value="Early Confirmation">Early Confirmation</option>
            <option value="Confirmation After Probation Extension">
              Confirmation After Probation Extension
            </option>
          </select>
        </FormField>

        <FormField
          label="Employment status after confirmation"
          required
          error={errors.employmentStatusAfterConfirmation}
        >
          <select
            {...register("employmentStatusAfterConfirmation", {
              required: "Employment status is required",
            })}
            className={inputClass}
          >
            <option value="">Select status</option>
            <option value="Permanent - Full Time">Permanent - Full Time</option>
            <option value="Permanent - Part Time">Permanent - Part Time</option>
            <option value="Contractual">Contractual</option>
            <option value="Consultant">Consultant</option>
          </select>
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Confirmed designation"
            required
            error={errors.confirmedDesignation}
          >
            <input
              {...register("confirmedDesignation", {
                required: "Confirmed designation is required",
              })}
              className={inputClass}
              placeholder="Enter confirmed designation"
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField
            label="Probation extension details, if applicable"
            error={errors.probationExtensionDetails}
          >
            <textarea
              {...register("probationExtensionDetails")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter probation extension details"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const SalaryEmploymentTerms = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Salary and Employment Terms
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Revised annual CTC" error={errors.revisedAnnualCTC}>
          <input
            type="text"
            inputMode="numeric"
            {...register("revisedAnnualCTC", {
              pattern: {
                value: /^[0-9]+$/,
                message: "Enter a valid amount",
              },
            })}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "");
            }}
            className={inputClass}
            placeholder="Enter revised annual CTC"
          />
        </FormField>

        <DateField
          name="salaryRevisionEffectiveDate"
          label="Salary revision effective date"
          control={control}
          errors={errors}
        />

        <FormField
          label="Additional benefits"
          error={errors.additionalBenefits}
        >
          <select {...register("additionalBenefits")} className={inputClass}>
            <option value="">Select benefits</option>
            <option value="Health Insurance">Health Insurance</option>
            <option value="Provident Fund">Provident Fund</option>
            <option value="Gratuity">Gratuity</option>
            <option value="Performance Bonus">Performance Bonus</option>
            <option value="Travel Allowance">Travel Allowance</option>
            <option value="Meal Allowance">Meal Allowance</option>
            <option value="Mobile and Internet Reimbursement">
              Mobile and Internet Reimbursement
            </option>
          </select>
        </FormField>

        <FormField
          label="Notice period after confirmation"
          required
          error={errors.noticePeriodAfterConfirmation}
        >
          <select
            {...register("noticePeriodAfterConfirmation", {
              required: "Notice period is required",
            })}
            className={inputClass}
          >
            <option value="">Select notice period</option>
            <option value="15 Days">15 Days</option>
            <option value="30 Days">30 Days</option>
            <option value="45 Days">45 Days</option>
            <option value="60 Days">60 Days</option>
            <option value="90 Days">90 Days</option>
          </select>
        </FormField>

        <FormField label="Working days" error={errors.workingDays}>
          <select {...register("workingDays")} className={inputClass}>
            <option value="">Select working days</option>
            <option value="Monday to Friday">Monday to Friday</option>
            <option value="Monday to Saturday">Monday to Saturday</option>
            <option value="5 Days a Week">5 Days a Week</option>
            <option value="6 Days a Week">6 Days a Week</option>
            <option value="Rotational Shift">Rotational Shift</option>
          </select>
        </FormField>

        <FormField label="Working hours" error={errors.workingHours}>
          <select {...register("workingHours")} className={inputClass}>
            <option value="">Select working hours</option>
            <option value="9:00 AM to 6:00 PM">9:00 AM to 6:00 PM</option>
            <option value="9:30 AM to 6:30 PM">9:30 AM to 6:30 PM</option>
            <option value="10:00 AM to 7:00 PM">10:00 AM to 7:00 PM</option>
            <option value="8 Hours per Day">8 Hours per Day</option>
            <option value="Flexible Hours">Flexible Hours</option>
          </select>
        </FormField>

        <FormField label="Leave entitlement" error={errors.leaveEntitlement}>
          <input
            {...register("leaveEntitlement")}
            className={inputClass}
            placeholder="Enter leave entitlement"
          />
        </FormField>

        <FormField
          label="Existing appointment terms continue"
          error={errors.existingTermsContinue}
        >
          <div className="flex h-11 items-center gap-6">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#10324F]">
              <input
                type="radio"
                value="Yes"
                {...register("existingTermsContinue")}
                className="h-4 w-4 accent-[#10324F]"
              />
              Yes
            </label>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#10324F]">
              <input
                type="radio"
                value="No"
                {...register("existingTermsContinue")}
                className="h-4 w-4 accent-[#10324F]"
              />
              No
            </label>
          </div>
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Additional employment conditions"
            error={errors.additionalEmploymentConditions}
          >
            <textarea
              {...register("additionalEmploymentConditions")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter additional employment conditions"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const PerformanceDetails = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Performance Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Performance rating" error={errors.performanceRating}>
          <select {...register("performanceRating")} className={inputClass}>
            <option value="">Select rating</option>
            <option value="Outstanding">Outstanding</option>
            <option value="Exceeds Expectations">Exceeds Expectations</option>
            <option value="Meets Expectations">Meets Expectations</option>
            <option value="Satisfactory">Satisfactory</option>
          </select>
        </FormField>

        <FormField
          label="Appreciation statement"
          error={errors.appreciationStatement}
        >
          <input
            {...register("appreciationStatement")}
            className={inputClass}
            placeholder="Enter appreciation statement"
          />
        </FormField>

        <FormField
          label="Key strengths or achievements"
          error={errors.keyStrengths}
        >
          <input
            {...register("keyStrengths")}
            className={inputClass}
            placeholder="Enter key strengths or achievements"
          />
        </FormField>

        <FormField
          label="Future expectations"
          error={errors.futureExpectations}
        >
          <input
            {...register("futureExpectations")}
            className={inputClass}
            placeholder="Enter future expectations"
          />
        </FormField>
      </div>
    </div>
  );
};

const LetterInformation = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Letter Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          name="confirmationLetterDate"
          label="Confirmation letter date"
          required
          control={control}
          errors={errors}
        />

        <FormField
          label="Authorised signatory name"
          required
          error={errors.signatoryName}
        >
          <input
            {...register("signatoryName", {
              required: "Signatory name is required",
            })}
            className={inputClass}
            placeholder="Enter signatory name"
          />
        </FormField>

        <FormField label="Reference number" error={errors.referenceNumber}>
          <input
            {...register("referenceNumber")}
            className={inputClass}
            placeholder="Enter reference number"
          />
        </FormField>

        <FormField
          label="Signatory designation"
          required
          error={errors.signatoryDesignation}
        >
          <input
            {...register("signatoryDesignation", {
              required: "Designation is required",
            })}
            className={inputClass}
            placeholder="Enter signatory designation"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Employee acknowledgement required"
            error={errors.acknowledgementRequired}
          >
            <div className="flex h-11 items-center gap-6">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-[#10324F]">
                <input
                  type="radio"
                  value="Yes"
                  {...register("acknowledgementRequired")}
                  className="h-4 w-4 accent-[#10324F]"
                />
                Yes
              </label>

              <label className="flex cursor-pointer items-center gap-2 text-sm text-[#10324F]">
                <input
                  type="radio"
                  value="No"
                  {...register("acknowledgementRequired")}
                  className="h-4 w-4 accent-[#10324F]"
                />
                No
              </label>
            </div>
          </FormField>
        </div>
      </div>
    </div>
  );
};

const ConfirmationLetterPreview = ({ values, logoPreview }) => {
  const formatDate = (date) => {
    if (!date) return "24 August, 2026";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return date;

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="rounded-xl border border-white shadow-sm backdrop-blur-2xl p-4">
      <h2 className="mb-5 text-sm font-semibold text-[#10324F]">Preview</h2>

      <div className="mx-auto w-full bg-white">
        <Ribbon position="top" />

        <div className="px-4 py-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company"
                className="h-8 max-w-25 object-contain"
              />
            ) : (
              <span className="text-lg text-[#10324F]">
                {values.companyName || "Company"}
              </span>
            )}
          </div>

          {/* Heading */}
          <div className="mt-3 text-center">
            <p className="text-xs font-bold text-[#10324F]">
              CONFIRMATION LETTER
            </p>
          </div>

          {/* Reference */}
          {values.referenceNumber && (
            <div className="mt-3 text-xs text-[#10324F]">
              <p>
                <strong>Reference No:</strong> {values.referenceNumber}
              </p>
            </div>
          )}

          {/* Recipient */}
          <div className="mt-3 text-xs leading-4 text-[#10324F]">
            <p className="font-semibold">To:</p>

            <p>{values.employeeName || "Employee Name"}</p>

            <p>{values.residentialAddress || "Employee Address"}</p>
          </div>

          {/* Body */}
          <div className="mt-3 text-xs leading-4 text-[#10324F]">
            <p>Dear {values.employeeName || "Employee Name"},</p>

            <p className="mt-3">
              We are pleased to inform you that, following the successful
              completion of your probation period
              {values.originalProbationPeriod && (
                <>
                  {" "}
                  of <strong>{values.originalProbationPeriod}</strong>
                </>
              )}{" "}
              with <strong>{values.companyName || "Company Name"}</strong>, your
              employment is hereby confirmed with effect from{" "}
              <strong>{formatDate(values.confirmationEffectiveDate)}</strong>.
            </p>

            <p className="mt-3">
              You joined the organization on{" "}
              <strong>{formatDate(values.joiningDate)}</strong> and your
              probation period was completed on{" "}
              <strong>{formatDate(values.probationCompletionDate)}</strong>.
            </p>

            <p className="mt-3">
              You will continue to serve as{" "}
              <strong>
                {values.confirmedDesignation ||
                  values.currentDesignation ||
                  "Designation"}
              </strong>
              {values.currentDepartment && (
                <>
                  {" "}
                  in the <strong>{values.currentDepartment}</strong> department
                </>
              )}
              {values.reportingManager && (
                <>
                  , reporting to <strong>{values.reportingManager}</strong>
                </>
              )}
              {values.employmentStatusAfterConfirmation && (
                <>
                  . Your employment status is now{" "}
                  <strong>{values.employmentStatusAfterConfirmation}</strong>
                </>
              )}
              .
            </p>

            {values.probationExtensionDetails && (
              <p className="mt-1">{values.probationExtensionDetails}</p>
            )}

            {values.revisedAnnualCTC && (
              <p className="mt-1">
                Your revised annual CTC is{" "}
                <strong>INR {values.revisedAnnualCTC}</strong>
                {values.salaryRevisionEffectiveDate && (
                  <>
                    , effective from{" "}
                    <strong>
                      {formatDate(values.salaryRevisionEffectiveDate)}
                    </strong>
                  </>
                )}
                .
              </p>
            )}

            {values.noticePeriodAfterConfirmation && (
              <p className="mt-1">
                Your notice period after confirmation is{" "}
                <strong>{values.noticePeriodAfterConfirmation}</strong>.
              </p>
            )}

            {values.existingTermsContinue === "Yes" && (
              <p className="mt-2">
                All other terms and conditions of your appointment letter remain
                unchanged and continue to apply.
              </p>
            )}

            {values.additionalEmploymentConditions && (
              <p className="mt-1">{values.additionalEmploymentConditions}</p>
            )}

            {values.performanceRating && (
              <p className="mt-1">
                Your performance during the probation period has been rated as{" "}
                <strong>{values.performanceRating}</strong>.
              </p>
            )}

            {values.appreciationStatement && (
              <p className="mt-1">{values.appreciationStatement}</p>
            )}

            {values.futureExpectations && (
              <p className="mt-1">{values.futureExpectations}</p>
            )}

            <p className="mt-1">
              We congratulate you on your confirmation and look forward to your
              continued contribution to the organization.
            </p>
          </div>

          {/* Date */}
          <div className="mt-3 text-xs text-[#10324F]">
            <p>Date: {formatDate(values.confirmationLetterDate)}</p>
          </div>

          {/* Signature */}
          <div className="mt-4 text-xs text-[#10324F]">
            <p>Sincerely,</p>

            <p className="mt-4 font-semibold">
              {values.signatoryName || "Authorised Signatory"}
            </p>

            <p>{values.signatoryDesignation || "HR Manager"}</p>

            <p>{values.companyName || "Company Name"}</p>
          </div>

          {/* Acknowledgement */}
          {values.acknowledgementRequired === "Yes" && (
            <div className="mt-4 text-xs leading-4 text-[#10324F]">
              <p className="font-semibold">Employee Acknowledgement</p>

              <p className="mt-1">
                I acknowledge receipt of this confirmation letter and accept the
                terms stated above.
              </p>

              <p className="mt-4">Signature: ____________ Date: ____________</p>
            </div>
          )}

          <div className="mt-8 text-center text-xs text-[#10324F]">
            Powered by Waggex
          </div>
        </div>

        <Ribbon position="bottom" />
      </div>
    </div>
  );
};

const Ribbon = ({ position = "top" }) => {
  const isTop = position === "top";

  return (
    <div className="relative -mx-px h-9.25 w-[calc(100%+2px)] overflow-hidden">
      {/* Green base */}
      <div className="absolute -inset-0.5 bg-[#10324F]" />

      <div
        className="absolute -inset-0.5"
        style={{
          transform: isTop ? "none" : "scaleX(-1) scaleY(-1)",
        }}
      >
        {/* Magenta ribbon */}
        <div
          className="absolute inset-0 bg-[#D1DFEA]"
          style={{
            clipPath:
              "polygon(76% 0%, 100% 0%, 100% 100%, 51% 100%, 47% 60%, 70% 55%)",
          }}
        />

        {/* White cut / separation */}
        <div
          className="absolute bottom-0 left-0 h-4 w-[55%] bg-white"
          style={{
            clipPath: "polygon(0 0, 88% 0, 100% 100%, 0 100%)",
          }}
        />
      </div>
    </div>
  );
};
